import React, { useState, useEffect } from 'react';
import { Box, Container, Grid, Typography, useTheme, SvgIconProps, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Edit as EditIcon,
  Timer as TimerIcon,
  Payments as PaymentsIcon,
  Support as SupportIcon,
} from '@mui/icons-material';

interface StepItemProps {
  number: number;
  title: string;
  icon: React.ReactElement<SvgIconProps>;
}

const StepItem: React.FC<StepItemProps> = ({ number, title, icon }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < parseInt(theme.breakpoints.values.md.toString()));
    };
    
    // Set initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, [theme.breakpoints.values.md]);
  
  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'row', md: 'column' },
        alignItems: { xs: 'flex-start', md: 'center' },
        textAlign: { xs: 'left', md: 'center' },
        height: '100%',
        position: 'relative',
        mx: 'auto',
        width: '100%',
        maxWidth: '280px',
        justifyContent: 'center',
        p: 3,
        borderRadius: 4,
        backgroundColor: 'rgba(26, 32, 44, 0.4)',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: `0 10px 20px rgba(74, 144, 226, 0.2)`,
        },
      }}
    >
      {/* Line connecting steps (for desktop) */}
      {number < 4 && !isMobile && (
        <Box
          sx={{
            position: 'absolute',
            top: '30%',
            right: '-50px',
            width: '50px',
            height: '2px',
            background: `linear-gradient(to right, rgba(74,144,226,0.5), rgba(74,144,226,0))`,
            display: { xs: 'none', md: 'block' },
            zIndex: 0,
          }}
        />
      )}
      
      {/* Step number circle */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(74,144,226,0.1)',
          border: '1px solid rgba(74,144,226,0.3)',
          mb: { xs: 0, md: 3 },
          mr: { xs: 3, md: 0 },
          zIndex: 1,
          position: 'relative',
        }}
      >
        {React.cloneElement(icon, {
          sx: { fontSize: 30, color: 'primary.main' }
        })}
      </Box>
      
      <Box sx={{ mt: { xs: 0, md: 2 }, textAlign: { xs: 'left', md: 'center' } }}>
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{
            color: 'primary.main',
            mb: 0.5,
            textAlign: { xs: 'left', md: 'center' }
          }}
        >
          {`${t('common.step')} ${number}`}
        </Typography>
        <Typography 
          variant="h6"
          sx={{
            fontWeight: 600,
            textAlign: { xs: 'left', md: 'center' }
          }}
        >
          {title}
        </Typography>
      </Box>
    </Paper>
  );
};

const HowItWorksSection: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  
  const steps = [
    {
      title: t('home.howItWorks.step1'),
      icon: <EditIcon />,
    },
    {
      title: t('home.howItWorks.step2'),
      icon: <TimerIcon />,
    },
    {
      title: t('home.howItWorks.step3'),
      icon: <PaymentsIcon />,
    },
    {
      title: t('home.howItWorks.step4'),
      icon: <SupportIcon />,
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 8, md: 10 },
        backgroundColor: 'background.default',
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at bottom left, rgba(74,144,226,0.05) 0%, transparent 70%)',
          zIndex: 0,
        },
      }}
    >
      <Container 
        maxWidth="lg"
        sx={{
          width: '100%',
          px: { xs: 2, sm: 3, md: 4 },
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box 
          sx={{ 
            mb: { xs: 6, md: 8 }, 
            textAlign: 'center',
            mx: 'auto',
            maxWidth: '800px'
          }}
        >
          <Typography 
            variant="h2" 
            gutterBottom
            sx={{
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 700,
              mb: 2,
            }}
          >
            {t('home.howItWorks.title')}
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: { xs: '1rem', md: '1.125rem' },
              mb: 4,
            }}
          >
            {t('home.howItWorksDescription')}
          </Typography>
        </Box>

        <Grid 
          container 
          spacing={4}
          sx={{ 
            maxWidth: '1200px',
            mx: 'auto',
            position: 'relative',
            justifyContent: 'center',
          }}
        >
          {steps.map((step, index) => (
            <Grid key={index} size={{ xs: 12, sm: 6, md: 3 }}>
              <StepItem number={index + 1} title={step.title} icon={step.icon} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default HowItWorksSection; 